import React, { FC, useState } from "react";

import { Translate } from "@/app/application/Translate";
import { ShiftEditableItemCard } from "@/app/application/admin-menu/settings/ShiftEditableItemCard";
import { ShiftSettingsSection } from "@/app/application/admin-menu/settings/ShiftSettingsSection";
import { ShiftSystem } from "@/features/shift/model/Shift/ShiftConfiguration";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import { Add as AddIcon, Settings as SettingsIcon } from "@mui/icons-material";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";

import {
	useMutateCreateShiftSystem,
	useMutateDeleteShiftSystem,
	useMutateUpdateShiftSystem,
} from "@/service/Settings/Mutations/ShiftSettingsMutations";

interface ShiftSystemSettingsProps {
	data: ShiftSystem[];
}

export const ShiftSystemSettings: FC<ShiftSystemSettingsProps> = ({ data }) => {
	const { createShiftSystem } = useMutateCreateShiftSystem();
	const { updateShiftSystem } = useMutateUpdateShiftSystem();
	const { deleteShiftSystem } = useMutateDeleteShiftSystem();

	const [editingId, setEditingId] = useState<number | "new" | null>(null);
	const [newName, setNewName] = useState("");
	const [saving, setSaving] = useState(false);

	const handleCreate = async () => {
		const name = newName.trim();
		if (!name) return;
		setSaving(true);
		try {
			await createShiftSystem({ Name: name });
			AlertRequest.success();
			setNewName("");
			setEditingId(null);
		} catch (e) {
			console.error(e);
			AlertRequest.failed();
		} finally {
			setSaving(false);
		}
	};

	const handleUpdate = async (system: ShiftSystem, name: string) => {
		if (!name.trim()) return;
		setSaving(true);
		try {
			await updateShiftSystem({ ...system, Name: name.trim() });
			AlertRequest.success();
			setEditingId(null);
		} catch (e) {
			console.error(e);
			AlertRequest.failed();
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (id: number) => {
		try {
			await AlertRequest.confirmAsync(deleteShiftSystem, id, "this system");
			setEditingId(null);
		} catch (e) {
			console.error(e);
			AlertRequest.failed();
		}
	};

	const handleAddClick = () => {
		setNewName("");
		setEditingId("new");
	};

	const handleCancelAdd = () => {
		setEditingId(null);
		setNewName("");
	};

	const systems = data ?? [];

	return (
		<ShiftSettingsSection
			title={
				<Translate String="Settings.Shift.Systems">Shift Systems</Translate>
			}
			icon={<SettingsIcon />}
			action={
				<Button
					variant="outlined"
					color="inherit"
					startIcon={<AddIcon />}
					onClick={handleAddClick}
					sx={{
						height: 32,
						minHeight: 32,
						px: 1.5,
						textTransform: "none",
						fontWeight: 600,
						borderColor: "divider",
						bgcolor: "background.paper",
						boxShadow: "none",
						whiteSpace: "nowrap",
					}}
				>
					<Translate String="Settings.Shift.System.AddSystem">
						Add System
					</Translate>
				</Button>
			}
			sx={{ width: "100%", height: "100%" }}
			headerSx={{
				alignItems: "center",
				pb: 2,
				borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
				mb: 2.5,
			}}
		>
			<Stack spacing={1.5}>
				{systems.map((system) => (
					<ShiftSystemItem
						key={system.Id}
						system={system}
						isEditing={editingId === system.Id}
						onStartEdit={() => setEditingId(system.Id)}
						onCancel={() => setEditingId(null)}
						onSave={(name) => {
							void handleUpdate(system, name);
						}}
						onDelete={() => {
							void handleDelete(system.Id);
						}}
						saving={saving}
					/>
				))}
				{editingId === "new" && (
					<ShiftEditableItemCard
						isEditing
						onEdit={() => {}}
						onSave={handleCreate}
						onCancel={handleCancelAdd}
						onDelete={() => {}}
						viewContent={null}
						editContent={
							<TextField
								autoFocus
								fullWidth
								size="small"
								placeholder="System name"
								value={newName}
								onChange={(e) => setNewName(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") void handleCreate();
									if (e.key === "Escape") handleCancelAdd();
								}}
								sx={{
									"& .MuiOutlinedInput-root": {
										bgcolor: (t) =>
											t.palette.mode === "dark"
												? "rgba(0,0,0,0.2)"
												: "rgba(0,0,0,0.04)",
										"& fieldset": { borderColor: "transparent" },
									},
								}}
							/>
						}
						isNew
						saving={saving}
					/>
				)}
				{systems.length === 0 && editingId !== "new" && (
					<Box py={4} textAlign="center" sx={{ color: "text.secondary" }}>
						<Typography variant="body2">
							No shift systems yet. Add one to get started.
						</Typography>
					</Box>
				)}
			</Stack>
		</ShiftSettingsSection>
	);
};

interface ShiftSystemItemProps {
	system: ShiftSystem;
	isEditing: boolean;
	onStartEdit: () => void;
	onCancel: () => void;
	onSave: (name: string) => void;
	onDelete: () => void;
	saving: boolean;
}

const ShiftSystemItem: FC<ShiftSystemItemProps> = ({
	system,
	isEditing,
	onStartEdit,
	onCancel,
	onSave,
	onDelete,
	saving,
}) => {
	const [editName, setEditName] = useState(system.Name);

	React.useEffect(() => {
		if (isEditing) setEditName(system.Name);
	}, [isEditing, system.Name]);

	const handleSave = () => {
		onSave(editName);
	};

	return (
		<ShiftEditableItemCard
			isEditing={isEditing}
			onEdit={onStartEdit}
			onSave={handleSave}
			onCancel={onCancel}
			onDelete={onDelete}
			viewContent={
				<Typography variant="body1" fontWeight={500} noWrap>
					{system.Name}
				</Typography>
			}
			editContent={
				<TextField
					autoFocus
					fullWidth
					size="small"
					value={editName}
					onChange={(e) => setEditName(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") handleSave();
						if (e.key === "Escape") onCancel();
					}}
					sx={{
						"& .MuiOutlinedInput-root": {
							bgcolor: (t) =>
								t.palette.mode === "dark"
									? "rgba(0,0,0,0.2)"
									: "rgba(0,0,0,0.04)",
							"& fieldset": { borderColor: "transparent" },
						},
					}}
				/>
			}
			saving={saving}
		/>
	);
};
